import React, { useMemo, useState } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Card, 
  CardContent, 
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Tabs,
  Tab
} from '@mui/material';
import { referralsApi } from '../../services/api/referrals.api';
import { aiSupportApi } from '../../services/api/ai-support.api';
import { useAuth } from '../../contexts/AuthContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export const AdditionalModulesTest: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<Record<string, any>>({});
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const auth = useAuth();

  const currentUserId = useMemo(() => {
    // Используем реальный userId; при отсутствии — валидный UUID-заглушку
    return auth?.user?.id || '00000000-0000-0000-0000-000000000001';
  }, [auth?.user?.id]);

  const testEndpoint = async (name: string, testFn: () => Promise<any>) => {
    setLoading(true);
    setError(null);
    try {
      const result = await testFn();
      setResults(prev => ({ ...prev, [name]: { success: true, data: result } }));
    } catch (err: any) {
      setResults(prev => ({ 
        ...prev, 
        [name]: { 
          success: false, 
          error: err.message || 'Неизвестная ошибка' 
        } 
      }));
      setError(`${name}: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Referrals API Tests
  const testReferralsStats = () => testEndpoint('Referrals Stats', () => 
    referralsApi.getStats(currentUserId)
  );

  const testReferralsInvites = () => testEndpoint('Referrals Invites', () => 
    referralsApi.getInvites(currentUserId)
  );

  const testReferralsEarnings = () => testEndpoint('Referrals Earnings', () => 
    referralsApi.getEarnings({ userId: currentUserId, page: 1, limit: 10 })
  );

  const testReferralsHistory = () => testEndpoint('Referrals History', () => 
    referralsApi.getHistory({ userId: currentUserId, page: 1, limit: 10 })
  );

  const testReferralsLevels = () => testEndpoint('Referrals Levels', () => 
    referralsApi.getLevels(currentUserId)
  );

  const testReferralsConfig = () => testEndpoint('Referrals Config', () => 
    referralsApi.getConfig()
  );

  const testReferralsAnalytics = () => testEndpoint('Referrals Analytics', () => 
    referralsApi.getAnalytics(currentUserId)
  );

  const testReferralsLeaderboard = () => testEndpoint('Referrals Leaderboard', () => 
    referralsApi.getLeaderboard()
  );

  // AI Support API Tests
  const testAiHealth = () => testEndpoint('AI Health Check', () => 
    aiSupportApi.getHealth()
  );

  const testAllReferralsEndpoints = async () => {
    await testReferralsStats();
    await testReferralsInvites();
    await testReferralsEarnings();
    await testReferralsHistory();
    await testReferralsLevels();
    await testReferralsConfig();
    await testReferralsAnalytics();
    await testReferralsLeaderboard();
  };

  const testAllEndpoints = async () => {
    await testAllReferralsEndpoints();
    await testAiHealth();
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const clearResults = () => {
    setResults({});
    setError(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        🔧 Тестирование дополнительных модулей
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Тестирование Referrals (18 endpoints) и AI-Support (1 endpoint) модулей
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="test tabs">
          <Tab label="Referrals API" />
          <Tab label="AI Support API" />
          <Tab label="Все результаты" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item>
            <Button 
              variant="contained" 
              onClick={testAllReferralsEndpoints}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              Тестировать все Referrals endpoints
            </Button>
          </Grid>
          <Grid item>
            <Button 
              variant="outlined" 
              onClick={testReferralsStats}
              disabled={loading}
            >
              Тест Stats
            </Button>
          </Grid>
          <Grid item>
            <Button 
              variant="outlined" 
              onClick={testReferralsInvites}
              disabled={loading}
            >
              Тест Invites
            </Button>
          </Grid>
          <Grid item>
            <Button 
              variant="outlined" 
              onClick={testReferralsEarnings}
              disabled={loading}
            >
              Тест Earnings
            </Button>
          </Grid>
          <Grid item>
            <Button 
              variant="outlined" 
              onClick={testReferralsLeaderboard}
              disabled={loading}
            >
              Тест Leaderboard
            </Button>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          {Object.entries(results)
            .filter(([name]) => name.includes('Referrals'))
            .map(([name, result]) => (
              <Grid item xs={12} md={6} key={name}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {name}
                    </Typography>
                    {result.success ? (
                      <Alert severity="success">
                        ✅ Успешно
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {JSON.stringify(result.data, null, 2)}
                        </Typography>
                      </Alert>
                    ) : (
                      <Alert severity="error">
                        ❌ Ошибка: {result.error}
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item>
            <Button 
              variant="contained" 
              onClick={testAiHealth}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              Тестировать AI Health
            </Button>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          {Object.entries(results)
            .filter(([name]) => name.includes('AI'))
            .map(([name, result]) => (
              <Grid item xs={12} md={6} key={name}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {name}
                    </Typography>
                    {result.success ? (
                      <Alert severity="success">
                        ✅ Успешно
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {JSON.stringify(result.data, null, 2)}
                        </Typography>
                      </Alert>
                    ) : (
                      <Alert severity="error">
                        ❌ Ошибка: {result.error}
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Box sx={{ mb: 3 }}>
          <Button 
            variant="contained" 
            onClick={testAllEndpoints}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
            sx={{ mr: 2 }}
          >
            Тестировать все endpoints
          </Button>
          <Button 
            variant="outlined" 
            onClick={clearResults}
            disabled={loading}
          >
            Очистить результаты
          </Button>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          {Object.entries(results).map(([name, result]) => (
            <Grid item xs={12} md={6} key={name}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {name}
                  </Typography>
                  {result.success ? (
                    <Alert severity="success">
                      ✅ Успешно
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {JSON.stringify(result.data, null, 2)}
                      </Typography>
                    </Alert>
                  ) : (
                    <Alert severity="error">
                      ❌ Ошибка: {result.error}
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {Object.keys(results).length === 0 && (
          <Card>
            <CardContent>
              <Typography variant="body1" color="text.secondary" align="center">
                Нажмите кнопку для начала тестирования
              </Typography>
            </CardContent>
          </Card>
        )}
      </TabPanel>
    </Box>
  );
};

export default AdditionalModulesTest;
